import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AnouncementsService from '../../../Api/AnouncementsService';
import CourseService from '../../../Api/CourseService';
import { toast } from 'react-toastify';
import Select from 'react-select';

const AddAnouncements = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const announcementId = new URLSearchParams(location.search).get('id');
    const announcementAPI = new AnouncementsService();
    const courseAPI = new CourseService();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [courses, setCourses] = useState([]);
    const [formData, setFormData] = useState({
        title: '',
        content: '',
        type: 'public',
        course_ids: []
    });

    // Fetch courses for dropdown
    useEffect(() => {
        const fetchCourses = async () => {
            try {
                const response = await courseAPI.GetAllCourseList();
                if (response.data && response.data.data) {
                    setCourses(response.data.data);
                }
            } catch (err) {
                console.error('Error loading courses:', err);
                toast.error('Failed to load courses');
            }
        };

        fetchCourses();
    }, []);

    // Transform courses for react-select format
    const courseOptions = courses.map(course => ({
        value: course.id,
        label: course.title
    }));

    // Get current value for react-select
    const selectedCourseValues = courseOptions.filter(option => 
        formData.course_ids && formData.course_ids.includes(Number(option.value))
    );

    // Fetch announcement data if editing
    useEffect(() => {
        const fetchAnnouncement = async () => {
            if (announcementId) {
                try {
                    setIsLoading(true);
                    const response = await announcementAPI.GetById(announcementId);
                    if (response.data && response.data.data) {
                        const announcementData = response.data.data;
                        setFormData({
                            title: announcementData.title || '',
                            content: announcementData.content || '',
                            type: announcementData.type || 'public',
                            course_ids: announcementData.course_ids 
                                ? announcementData.course_ids.map(id => Number(id))
                                : []
                        });
                    }
                } catch (err) {
                    setError('Failed to load announcement data. Please try again.');
                    console.error('Error loading announcement:', err);
                } finally {
                    setIsLoading(false);
                }
            }
        };

        fetchAnnouncement();
    }, [announcementId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'type' && value === 'public') {
            setFormData(prev => ({
                ...prev,
                [name]: value,
                course_ids: [] // Clear course selections when type is public
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    // Update handleCourseSelection for react-select
    const handleCourseSelection = (selectedOptions) => {
        const selectedIds = selectedOptions 
            ? selectedOptions.map(option => Number(option.value))
            : [];
        setFormData(prev => ({
            ...prev,
            course_ids: selectedIds
        }));
    };

    // Custom styles for react-select
    const customStyles = {
        control: (provided, state) => ({
            ...provided,
            minHeight: '56px',
            backgroundColor: '#F9FAFB',
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            boxShadow: state.isFocused ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none',
            '&:hover': {
                borderColor: '#3b82f6'
            }
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#F3F4F6' : 'white',
            color: state.isSelected ? 'white' : '#374151',
            padding: '10px 12px',
            cursor: 'pointer'
        }),
        multiValue: (provided) => ({
            ...provided,
            backgroundColor: '#EFF6FF',
            borderRadius: '6px',
            padding: '2px'
        }),
        multiValueLabel: (provided) => ({
            ...provided,
            color: '#3b82f6',
            fontSize: '0.875rem'
        }),
        multiValueRemove: (provided) => ({
            ...provided,
            color: '#3b82f6',
            '&:hover': {
                backgroundColor: '#DBEAFE',
                color: '#2563EB'
            }
        })
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            // Validate course selection for course type
            if (formData.type === 'course' && (!formData.course_ids || formData.course_ids.length === 0)) {
                throw new Error('Please select at least one course for course-specific announcement');
            }

            const submitData = {
                ...formData,
                course_ids: formData.type === 'public' ? [] : formData.course_ids
            };

            let response;
            if (announcementId) {
                response = await announcementAPI.UpdateByID(submitData, announcementId);
            } else {
                response = await announcementAPI.AddAnouncements(submitData);
            }

            if (response.data.status === "success") {
                toast.success(announcementId ? 'Announcement updated successfully!' : 'Announcement created successfully!');
                navigate('/announcements-list');
            } else {
                throw new Error(response.data.message || 'Failed to save announcement');
            }
        } catch (error) {
            setError(error.response?.data?.message || error.message || 'Failed to save announcement');
            toast.error(error.response?.data?.message || 'An error occurred while saving');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-body">
                <h4 className="text-xl text-primary-light mb-24">
                    {announcementId ? 'Edit Announcement' : 'Add New Announcement'}
                </h4>
                {error && (
                    <div className="alert alert-danger mb-3" role="alert">
                        {error}
                    </div>
                )}
                {isLoading && !formData.title ? (
                    <div className="text-center p-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <div className="mb-3">
                            <label className="form-label">
                                Title <span className="text-danger">*</span>
                            </label>
                            <input
                                type="text"
                                className="form-control h-56-px bg-neutral-50 radius-12"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                placeholder="Enter announcement title"
                                required
                            />
                        </div>

                        <div className="mb-3">
                            <label className="form-label">
                                Content <span className="text-danger">*</span>
                            </label>
                            <textarea
                                className="form-control h-120-px bg-neutral-50 radius-12"
                                name="content"
                                value={formData.content}
                                onChange={handleChange}
                                placeholder="Enter announcement content"
                                rows="4"
                                required
                            />
                        </div>

                        <div className="mb-3">
                            <label className="form-label">
                                Type <span className="text-danger">*</span>
                            </label>
                            <select
                                className="form-control h-56-px bg-neutral-50 radius-12"
                                name="type"
                                value={formData.type}
                                onChange={handleChange}
                                required
                            >
                                <option value="public">Public</option>
                                <option value="course">Course</option>
                            </select>
                        </div>

                        <div className="mb-3">
                            <label className="form-label">
                                Select Courses {formData.type === 'course' && <span className="text-danger">*</span>}
                            </label>
                            <Select
                                isMulti
                                name="course_ids"
                                options={courseOptions}
                                value={selectedCourseValues}
                                onChange={handleCourseSelection}
                                styles={customStyles}
                                placeholder="Select courses..."
                                noOptionsMessage={() => "No courses available"}
                                className="react-select-container"
                                classNamePrefix="react-select"
                                isDisabled={formData.type === 'public'}
                                required={formData.type === 'course'}
                            />
                            {error && formData.type === 'course' && !formData.course_ids.length && (
                                <div className="invalid-feedback d-block">
                                    Please select at least one course
                                </div>
                            )}
                        </div>

                        <div className="d-flex align-items-center justify-content-center gap-3 mt-32">
                            <button
                                type="button"
                                className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-11 radius-8"
                                onClick={() => navigate('/announcements-list')}
                                disabled={isLoading}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        {announcementId ? 'Updating...' : 'Save Announcement'}
                                    </>
                                ) : (
                                    announcementId ? 'Update Announcement' : 'Save Announcement'
                                )}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default AddAnouncements; 